import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Package, Users, TrendingUp, DollarSign, Info, Truck, PlayCircle, CheckCircle2, XCircle, Pencil, Save } from "lucide-react";
import Navbar from "../components/Navbar";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdminPricing from "./AdminPricing";
import AdminSimulator from "./AdminSimulator";
import AdminContent from "./AdminContent";

// Recursively convert numeric-looking strings to numbers before saving config.
const numify = (v) => {
  if (Array.isArray(v)) return v.map(numify);
  if (v && typeof v === "object") {
    const o = {};
    for (const k in v) o[k] = numify(v[k]);
    return o;
  }
  if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) return Number(v);
  return v;
};

function Field({ label, tip, children }) {
  return (
    <div>
      <label className="text-xs font-bold text-nbblack/60 flex items-center gap-1">
        {label}
        {tip && (
          <span title={tip} className="cursor-help text-nbblack/40 hover:text-nbpink">
            <Info size={13} />
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full mt-1 px-3 py-2 rounded-lg nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-semibold";


const FULFILL = {
  pending_production: { label: "Pending production", cls: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  in_production: { label: "In production", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  complete: { label: "Complete", cls: "bg-green-100 text-green-700 border-green-300" },
  canceled: { label: "Canceled", cls: "bg-red-100 text-red-700 border-red-300" },
};

function OrderRow({ o, carriers, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState(carriers[0] || "");
  const [tracking, setTracking] = useState("");
  const [editShip, setEditShip] = useState(false);
  const [shipBusy, setShipBusy] = useState(false);
  const emptyShip = { full_name: "", email: "", phone: "", address1: "", address2: "", city: "", province: "", postal_code: "", country: "CA" };
  const [shipForm, setShipForm] = useState({ ...emptyShip, ...(o.shipping || {}) });
  const setSF = (k, v) => setShipForm((s) => ({ ...s, [k]: v }));

  const shipEditable = o.fulfillment_status !== "complete" && o.fulfillment_status !== "canceled" && o.status !== "canceled";

  const saveShipping = async () => {
    setShipBusy(true);
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/shipping`, shipForm);
      toast.success("Shipping address updated");
      onUpdate(o.id, { shipping: data.shipping, ship_country: data.shipping.country, ship_province: data.shipping.province });
      setEditShip(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setShipBusy(false);
    }
  };

  const paid = o.status === "paid";
  const fs = o.fulfillment_status || "pending_production";

  const change = async (target, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/status`, { fulfillment_status: target, ...extra });
      toast.success(`Order ${target === "canceled" ? "canceled" : "updated"}`);
      onUpdate(o.id, data);
      setShowComplete(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const completeSubmit = () => {
    if (!carrier || !tracking.trim()) {
      toast.error("Carrier and tracking number are required");
      return;
    }
    change("complete", { carrier, tracking_number: tracking.trim() });
  };

  return (
    <div className="p-4" data-testid="admin-order-row">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-head font-bold text-lg" data-testid="order-display-id">
            {o.display_id || <span className="text-nbblack/40">Awaiting payment</span>}
          </p>
          <p className="text-sm text-nbblack/50">{o.user_email} · {new Date(o.created_at).toLocaleString()} · {o.items.length} item(s)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${paid ? "bg-green-100 text-green-700 border-green-300" : "bg-yellow-100 text-yellow-700 border-yellow-300"}`}>
            {paid ? "Paid" : "Unpaid"}
          </span>
          {paid && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${FULFILL[fs].cls}`} data-testid="order-fulfillment-badge">
              {FULFILL[fs].label}
            </span>
          )}
          <span className="font-head font-extrabold text-xl">${o.total.toFixed(2)}</span>
        </div>
      </div>

      {(o.shipping || shipEditable) && (
        <div className="mt-3 bg-[#FFF9F0] nb-border rounded-xl p-3 text-sm" data-testid="order-shipping-address">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="font-head font-bold text-xs uppercase tracking-wide text-nbblack/50">Ship to</p>
            {shipEditable && !editShip && (
              <button onClick={() => { setShipForm({ ...emptyShip, ...(o.shipping || {}) }); setEditShip(true); }} data-testid="edit-shipping-btn" className="inline-flex items-center gap-1 text-xs font-bold text-nbpink hover:underline">
                <Pencil size={12} /> Edit
              </button>
            )}
          </div>
          {!editShip ? (
            <>
              <p className="font-bold" data-testid="ship-name">{o.shipping?.full_name || "—"}</p>
              <p className="text-nbblack/70" data-testid="ship-contact">
                {o.shipping?.email || o.user_email}{o.shipping?.phone ? ` · ${o.shipping.phone}` : ""}
              </p>
              <p className="text-nbblack/70" data-testid="ship-street">
                {o.shipping?.address1}{o.shipping?.address2 ? `, ${o.shipping.address2}` : ""}
              </p>
              <p className="text-nbblack/70" data-testid="ship-region">
                {[o.shipping?.city, o.shipping?.province, o.shipping?.postal_code].filter(Boolean).join(", ")}
                {o.shipping?.country ? ` · ${o.shipping.country}` : ""}
              </p>
            </>
          ) : (
            <div className="space-y-2" data-testid="edit-shipping-form">
              <input value={shipForm.full_name} onChange={(e) => setSF("full_name", e.target.value)} placeholder="Full name" className={inputCls} data-testid="edit-ship-name" />
              <div className="grid grid-cols-2 gap-2">
                <input value={shipForm.email} onChange={(e) => setSF("email", e.target.value)} placeholder="Email" className={inputCls} data-testid="edit-ship-email" />
                <input value={shipForm.phone} onChange={(e) => setSF("phone", e.target.value)} placeholder="Phone" className={inputCls} data-testid="edit-ship-phone" />
              </div>
              <input value={shipForm.address1} onChange={(e) => setSF("address1", e.target.value)} placeholder="Street address" className={inputCls} data-testid="edit-ship-address1" />
              <input value={shipForm.address2} onChange={(e) => setSF("address2", e.target.value)} placeholder="Apt, suite (optional)" className={inputCls} data-testid="edit-ship-address2" />
              <div className="grid grid-cols-2 gap-2">
                <input value={shipForm.city} onChange={(e) => setSF("city", e.target.value)} placeholder="City" className={inputCls} data-testid="edit-ship-city" />
                <input value={shipForm.province} onChange={(e) => setSF("province", e.target.value)} placeholder="Province / State" className={inputCls} data-testid="edit-ship-province" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={shipForm.postal_code} onChange={(e) => setSF("postal_code", e.target.value)} placeholder="Postal / ZIP" className={inputCls} data-testid="edit-ship-postal" />
                <input value={shipForm.country} onChange={(e) => setSF("country", e.target.value)} placeholder="Country" className={inputCls} data-testid="edit-ship-country" />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button disabled={shipBusy} onClick={saveShipping} data-testid="save-shipping-btn" className="inline-flex items-center gap-1.5 bg-green-400 font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                  {shipBusy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save
                </button>
                <button disabled={shipBusy} onClick={() => setEditShip(false)} className="font-head font-bold text-sm px-3 py-2 rounded-full nb-border nb-press bg-white">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={() => setOpen((v) => !v)} data-testid="toggle-order-items" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-nbblack/60 hover:text-nbpink">
        <Package size={15} /> {open ? "Hide" : "View"} files & specs ({o.items.length})
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {o.items.map((it, idx) => {
            const printedSize = `${it.width_in}" × ${it.height_in}"`;
            return (
              <div key={idx} className="bg-[#FFF9F0] nb-border rounded-xl p-3 flex gap-4 flex-wrap" data-testid="order-item-detail">
                {/* Original PNG */}
                <div className="text-center">
                  <div className="w-24 h-24 rounded-lg bg-white nb-border overflow-hidden flex items-center justify-center checkerboard">
                    {it.original_path ? <img src={fileUrl(it.original_path)} alt="original artwork" className="w-full h-full object-contain p-1" /> : <span className="text-xs text-nbblack/40">n/a</span>}
                  </div>
                  {it.original_path ? (
                    <a href={fileUrl(it.original_path)} target="_blank" rel="noreferrer" download data-testid="download-original" className="text-xs font-bold text-nbpink hover:underline mt-1 inline-block">Download PNG</a>
                  ) : <p className="text-xs text-nbblack/40 mt-1">Original PNG</p>}
                </div>
                {/* Mockup / preview */}
                <div className="text-center">
                  <div className="w-24 h-24 rounded-lg bg-white nb-border overflow-hidden flex items-center justify-center">
                    {it.preview_path ? <img src={fileUrl(it.preview_path)} alt="mockup" className="w-full h-full object-contain p-1" /> : <span className="text-xs text-nbblack/40">n/a</span>}
                  </div>
                  {it.preview_path ? (
                    <a href={fileUrl(it.preview_path)} target="_blank" rel="noreferrer" download data-testid="download-mockup" className="text-xs font-bold text-nbpink hover:underline mt-1 inline-block">Download mockup</a>
                  ) : <p className="text-xs text-nbblack/40 mt-1">Mockup</p>}
                </div>
                {/* Specs */}
                <div className="text-sm flex-1 min-w-[200px]">
                  <p className="font-bold">{it.material_name} · {it.cut_name}</p>
                  <p className="text-nbblack/60">Sticker size (footprint): <b>{printedSize}</b></p>
                  {it.outline_in != null && (
                    <p className="text-nbblack/60" data-testid="item-outline">Rendered cut outline: <b>{it.outline_in}"</b> per side</p>
                  )}
                  {it.artwork_width != null && (
                    <p className="text-nbblack/60">Artwork inside: <b>{it.artwork_width}" × {it.artwork_height}"</b></p>
                  )}
                  <p className="text-nbblack/60">Quantity: <b>{it.quantity}</b>{it.per_sheet ? <span> · {it.per_sheet}/sheet · {it.total_stickers} stickers</span> : null}</p>
                  <p className="text-nbblack/60">Unit: ${it.unit_price?.toFixed(2)} · Line: <b>${it.line_total?.toFixed(2)}</b></p>
                  {it.message && <p className="text-xs text-nbblack/50 italic mt-1">“{it.message}”</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {paid && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {fs === "pending_production" && (
            <>
              <button disabled={busy} onClick={() => change("in_production")} data-testid="btn-start-production" className="inline-flex items-center gap-1.5 bg-nbcyan font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                <PlayCircle size={16} /> Start production
              </button>
              <button disabled={busy} onClick={() => change("canceled")} data-testid="btn-cancel-order" className="inline-flex items-center gap-1.5 bg-white text-red-600 font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                <XCircle size={16} /> Cancel
              </button>
            </>
          )}
          {fs === "in_production" && !showComplete && (
            <>
              <button disabled={busy} onClick={() => setShowComplete(true)} data-testid="btn-mark-complete" className="inline-flex items-center gap-1.5 bg-green-400 font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                <CheckCircle2 size={16} /> Mark complete
              </button>
              <button disabled={busy} onClick={() => change("canceled")} data-testid="btn-cancel-order" className="inline-flex items-center gap-1.5 bg-white text-red-600 font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                <XCircle size={16} /> Cancel
              </button>
            </>
          )}
          {fs === "in_production" && showComplete && (
            <div className="w-full bg-[#FFF9F0] nb-border rounded-xl p-4 flex items-end gap-3 flex-wrap">
              <Field label="Carrier" tip="Shipping carrier used for delivery.">
                <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className={inputCls + " w-44"} data-testid="carrier-select">
                  {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Tracking number" tip="Carrier tracking number shown to the customer.">
                <input value={tracking} onChange={(e) => setTracking(e.target.value)} className={inputCls + " w-56"} placeholder="e.g. 1Z999AA10123456784" data-testid="tracking-input" />
              </Field>
              <button disabled={busy} onClick={completeSubmit} data-testid="btn-confirm-complete" className="inline-flex items-center gap-1.5 bg-green-400 font-head font-bold text-sm px-4 py-2 rounded-full nb-border nb-press disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Confirm & ship
              </button>
              <button disabled={busy} onClick={() => setShowComplete(false)} className="font-head font-bold text-sm px-3 py-2 rounded-full nb-border nb-press bg-white">Cancel</button>
            </div>
          )}
          {fs === "complete" && (
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-nbblack/70" data-testid="order-shipping-info">
              <Truck size={16} /> {o.carrier} · {o.tracking_number}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("pricing");
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (authLoading || !user || user.role !== "admin") return;
    api.get("/pricing/config").then(({ data }) => setCfg(data));
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/carriers").then(({ data }) => setCarriers(data.carriers)).catch(() => {});
  }, [user, authLoading]);

  const onOrderUpdate = (id, patch) =>
    setOrders((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = numify({ ...cfg });
      if (payload.holidays) payload.holidays = payload.holidays.filter(Boolean);
      const { data } = await api.put("/admin/pricing", payload);
      setCfg(data);
      toast.success("Pricing saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return <div className="App"><Navbar /><div className="flex justify-center py-32"><Loader2 className="animate-spin" size={40} /></div></div>;
  }
  if (!user || user.role !== "admin") {
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-24 text-center">
          <p className="font-head font-bold text-xl mb-6">Admin access only.</p>
          <Link to="/" className="inline-flex bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">Go home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-head font-extrabold text-4xl sm:text-5xl mb-8">Admin dashboard</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: DollarSign, label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", bg: "bg-nbyellow" },
            { icon: Package, label: "Paid orders", value: stats?.paid_orders ?? "—", bg: "bg-nbcyan" },
            { icon: TrendingUp, label: "Total orders", value: stats?.total_orders ?? "—", bg: "bg-white" },
            { icon: Users, label: "Customers", value: stats?.total_users ?? "—", bg: "bg-white" },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} nb-border nb-shadow rounded-2xl p-5`}>
              <s.icon size={24} className="mb-2" />
              <p className="font-head font-extrabold text-3xl">{s.value}</p>
              <p className="text-sm font-semibold text-nbblack/60">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-6">
          {["pricing", "orders", "simulator", "content"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`font-head font-bold px-5 py-2 rounded-full nb-border nb-press capitalize ${tab === t ? "bg-nbpink text-white nb-shadow-sm" : "bg-white"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "pricing" && cfg && (
          <AdminPricing cfg={cfg} setCfg={setCfg} saving={saving} onSave={save} />
        )}

        {tab === "simulator" && cfg && <AdminSimulator cfg={cfg} />}

        {tab === "content" && content && (
          <AdminContent content={content} setContent={setContent} materials={cfg?.materials || []} />
        )}

        {tab === "orders" && (
          <div className="bg-white nb-border nb-shadow rounded-2xl overflow-hidden">
            {orders.length === 0 ? (
              <p className="p-10 text-center font-head font-bold text-nbblack/50">No orders yet.</p>
            ) : (
              <div className="divide-y-2 divide-nbblack/10">
                {orders.map((o) => (
                  <OrderRow key={o.id} o={o} carriers={carriers} onUpdate={onOrderUpdate} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
